// =============================================================================
// PageHeader (WS-9 / UX-audit §5.1) — the shared sticky page header shell + a
// 44px, never-dead-end back button (<BackButton>, which owns the smart-back
// logic). Replaces the ad-hoc `<header class="sticky top-0 …"><button p-2 …>
// <ArrowLeft/></button> … </header>` pattern repeated across ~200 pages, which
// gave a ~36px back target (sub-HIG) and split inconsistently between
// navigate('/') and navigate(-1) — the latter dead-ending a deep-linked page.
//
// §5.1 made this the app's insertion point:
//   - `title` renders the standard h1 AND sets the browser-tab title
//     (usePageTitle) — one prop closes both gaps. A page with a custom title
//     block keeps passing children and uses `tabTitle` for the tab.
//   - A contextual <HelpButton/> renders automatically whenever the route
//     resolves a help topic (resolveHelpTopicId) — help reach without
//     per-page wiring. Opt out with `noHelp` if the page owns its help UI.
//   - `staleUpdatedAt` (the primary query's dataUpdatedAt) renders the
//     offline age cue (<StaleDataNotice/>) in the header.
//   - A "Go to" button (<HelpBarButton variant="header"/>, the ⌘K palette)
//     sits beside the back arrow on every page (W-14 / X-04). Under touch the
//     desktop rail is hidden and a tablet has no keyboard, so without it the
//     only way from one module to another was Back → Home → scroll → tile.
//     It sits on the LEFT, paired with Back, because the right end of a
//     wrapping header moves: after a full-width action strip (Scouting) it
//     landed on a third row by itself.
//   - It publishes its own height as `--page-header-h` on its parent (the page
//     root), 0px while it is not sticky (`short:static`), so a second sticky
//     bar — a week header, a search bar — can pin directly UNDER it with
//     useStickyBelowPageHeader instead of sliding beneath it at top-0 (farm
//     audit X-14, W-18). See src/hooks/usePageHeaderOffset.ts.
//   - `title` + `icon` + `subtitle` + `badge` + `actions` (packhouse audit
//     SH-05/SH-06, PW-13). A page that passed its own title block beside its
//     buttons had it crushed — Employee report's h1 measured 0px, a pallet's id
//     read "KF49-26092…", a grower "Kromrivi" — and the WHOLE header was
//     sticky, so rows of wrapped actions stayed pinned (Stock 189px, New pallet
//     197px). Now the title block always keeps its row, and on a phone the
//     actions render on their own row UNDER the header that scrolls away with
//     the page: only the title row pins. check-pageheader-titles flags an
//     `<h1>` still passed as a child.
//
// Back behaviour (see BackButton): onBack > backTo > smart back.
// =============================================================================

import { useRef, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import BackButton from '@/components/BackButton'
import HelpButton from '@/components/HelpButton'
import { HelpBarButton } from '@/components/HelpBarButton'
import { StaleDataNotice } from '@/components/StaleDataNotice'
import { resolveHelpTopicId } from '@/lib/helpTopics'
import { usePageTitle } from '@/hooks/usePageTitle'
import { usePublishPageHeaderHeight } from '@/hooks/usePageHeaderOffset'
import { useMediaQuery } from '@/hooks/useMediaQuery'

export default function PageHeader({
  onBack, backTo, title, icon, subtitle, badge, actions, tabTitle, noHelp = false, printHidden = false, dark = false, staleUpdatedAt, children,
}: {
  onBack?: () => void
  backTo?: string
  /** Standard title: renders the shared h1 and sets the browser-tab title. */
  title?: string
  /** A glyph before the title (a lucide icon element). */
  icon?: ReactNode
  /** One line of context under the title ("RUN-0412 · Kromrivier · Nadorcott").
   *  Truncates rather than wrapping, and drops on a short (landscape phone)
   *  screen like every header subtitle. */
  subtitle?: ReactNode
  /** A chip that belongs to the title — a status, an ownership tag. Beside the
   *  title from sm; on a phone it leads the subtitle line, so it can never
   *  squeeze the title it describes. */
  badge?: ReactNode
  /** The page's buttons. In the title row from sm; on a phone, on their own
   *  row UNDER the header that scrolls away with the page — the pinned header
   *  stays one row. Give each a visible word: a tooltip never shows on touch. */
  actions?: ReactNode
  /** Tab title only — for pages whose visible title block is custom children. */
  tabTitle?: string
  /** Opt out of the auto route-resolved help button (page owns its help UI). */
  noHelp?: boolean
  /** Hide the header on print — for pages whose body IS the printed artefact. */
  printHidden?: boolean
  /** Keep the band dark whatever the page theme: for a page whose root is
   *  `data-theme="light"` (a designer, a report) that has always had a dark header. */
  dark?: boolean
  /** The page's primary query `dataUpdatedAt` → offline "data from N ago" cue. */
  staleUpdatedAt?: number
  /** Anything else for the pinned row. Not a title: pass `title`/`subtitle`
   *  (check-pageheader-titles), and not the page's buttons: pass `actions`. */
  children?: ReactNode
}) {
  usePageTitle(title ?? tabTitle)
  const ref = useRef<HTMLElement>(null)
  usePublishPageHeaderHeight(ref)
  // Where the actions and the badge go is a choice of TREE, not of style: each
  // renders once (a hidden second copy would mount every button twice).
  const wide = useMediaQuery('(min-width: 640px)')
  const [inRow, below] = wide ? [actions, null] : [null, actions]
  const print = printClass(printHidden)
  return (
    <>
    <header ref={ref} data-theme={islandTheme(dark)} className={HEADER_CLASS + print} style={HEADER_STYLE}>
      <BackButton onClick={onBack} to={backTo} />
      <HelpBarButton variant="header" />
      <HeaderTitle title={title} icon={icon} subtitle={subtitle} badge={badge} badgeBesideTitle={wide} />
      <InlineActions actions={inRow} />
      {children}
      <HeaderEnd staleUpdatedAt={staleUpdatedAt} noHelp={noHelp} />
    </header>
    <ActionsRow actions={below} print={print} dark={dark} />
    </>
  )
}

const printClass = (hidden: boolean) => (hidden ? ' print:hidden' : '')

// A page whose root is data-theme="light" passes `dark` to keep today's dark band.
const islandTheme = (dark: boolean) => (dark ? 'dark' : undefined)

// flex-wrap: action-heavy headers (Stock's 8 icon buttons, Precool's 5)
// wrapped to a second row on phones before the codemod; keep that.
// short: a phone on its side is 390px tall, and a pinned 77–133px header
// (Scouting's wrapped to two rows) is a fifth to a third of it (farm audit
// X-02). There the header scrolls away with the page, and the one-line
// subtitle under the title — `<h1>` then `<p>`, the idiom every page with
// a subtitle uses — is dropped, as it already is on a phone held upright.
// [&>.flex-1.min-w-0]: the same 7rem title floor as `title=` (below),
// for pages that pass their own `flex-1 min-w-0` title block. Those were
// licensed to collapse, and the Go-to button costs row 1 another 44px:
// at 390 Fuel's and Chemicals' titles ran under their action buttons.
// It only matches a child that is BOTH flex-1 and min-w-0, so a block
// with its own larger floor keeps it.
// text-fg: the band follows the page theme (surface-sunk and text-fg are tokens),
// and it owns its text colour. Inherited, a light page's `text-gray-900` root
// painted the Back arrow gray-900 on gray-900 — present, tappable and
// invisible on Printing setup, Label designer, Stock labels and Packing
// lists (packhouse audit PM-PAL-31, PM-STK-17, PW-18). text-fg is the same
// colour as the shell's body text (gray-100 in dark), so dark pages do not change.
const HEADER_CLASS = 'sticky top-0 z-10 surface-sunk text-fg border-b hairline px-4 pb-3 flex flex-wrap items-center gap-3 [&>.flex-1.min-w-0]:min-w-[7rem] short:static short:[&_h1+p]:hidden'

// Safe-area top lives on <body> (index.css) for in-flow content, but a
// min-h-full page window-scrolls that padding away and this sticky
// header pins at physical top:0 — under the notch in the installed PWA,
// and under the fixed TopBanners strip when "Connection lost" is up.
// So the header carries the inset itself (+ the 12px that was py-3's
// top half) and cancels the body padding with an equal negative margin,
// keeping the unscrolled position unchanged. --top-inset is the ONE
// expression (notch ∨ banner strip) that <body> and .sticky-top-inset
// read too, defined in index.css. Inline style, not a Tailwind arbitrary
// class: env() bracket-classes have silently generated zero CSS before.
const HEADER_STYLE = {
  paddingTop: 'calc(var(--top-inset) + 12px)',
  marginTop:  'calc(-1 * var(--top-inset))',
} as const

// min-w-[7rem], NOT min-w-0. `min-w-0` licenses the title box to collapse to
// nothing, and beside enough shrink-0 actions it always does: the 2026-08-14
// header sweep found Stock rendering as "S." and Dispatch truncated, at 375px,
// on a header that had already wrapped to a second row — so the width was
// there. The floor makes the actions wrap instead of the page losing its name;
// `truncate` still applies past 7rem. A page's own `flex-1 min-w-0` title <div>
// gets the same floor from the header's `[&>.flex-1.min-w-0]` rule.
function HeaderTitle({ title, icon, subtitle, badge, badgeBesideTitle }: TitleProps) {
  if (icon != null || subtitle != null || badge != null) {
    return <TitleBlock title={title} icon={icon} subtitle={subtitle} badge={badge} badgeBesideTitle={badgeBesideTitle} />
  }
  return title != null ? <h1 className="text-lg font-bold flex-1 min-w-[7rem] truncate">{title}</h1> : null
}

// The actions in the title row (sm and up).
function InlineActions({ actions }: { actions?: ReactNode }) {
  if (actions == null) return null
  return <div className="flex flex-wrap items-center justify-end gap-2">{actions}</div>
}

// A phone's actions: a row UNDER the header, not sticky, so it scrolls away with
// the page and the header above it stays one row (SH-06 — Stock's pinned block
// was 189px).
function ActionsRow({ actions, print, dark }: { actions?: ReactNode; print: string; dark: boolean }) {
  if (actions == null) return null
  return (
    <div data-page-header-actions="" data-theme={islandTheme(dark)}
      className={`surface-sunk text-fg border-b hairline px-4 py-2 flex flex-wrap items-center gap-2${print}`}>
      {actions}
    </div>
  )
}

// The row's right end: the offline age cue, then the route's help topic.
function HeaderEnd({ staleUpdatedAt, noHelp }: { staleUpdatedAt?: number; noHelp: boolean }) {
  const { pathname } = useLocation()
  const helpTopic = noHelp ? null : resolveHelpTopicId(pathname)
  return (
    <>
      {staleUpdatedAt != null && <StaleDataNotice dataUpdatedAt={staleUpdatedAt} className="shrink-0" />}
      {helpTopic && <HelpButton className="shrink-0 -my-2" />}
    </>
  )
}

type TitleProps = {
  title?: string
  icon?: ReactNode
  subtitle?: ReactNode
  badge?: ReactNode
  badgeBesideTitle: boolean
}

// The title with what belongs to it. The block holds the 7rem floor, and the
// h1 inside it the whole first line, so neither a badge nor the page's buttons
// can crush it: they sit beside it only where there is room.
function TitleBlock({ title, icon, subtitle, badge, badgeBesideTitle }: TitleProps) {
  const badgeEl = badge != null ? <span className="shrink-0 inline-flex items-center">{badge}</span> : null
  return (
    <div className="flex-1 min-w-[7rem]">
      <div className="flex items-center gap-2 min-w-0">
        {icon != null && <span className="shrink-0 inline-flex" aria-hidden="true">{icon}</span>}
        {title != null && <h1 className="text-lg font-bold truncate min-w-0">{title}</h1>}
        {badgeBesideTitle && badgeEl}
      </div>
      <TitleSubline lead={badgeBesideTitle ? null : badgeEl} subtitle={subtitle} />
    </div>
  )
}

// The one line under the title: on a phone the badge leads it, then the
// subtitle. Dropped on a short (landscape phone) screen like every subtitle.
function TitleSubline({ lead, subtitle }: { lead: ReactNode; subtitle?: ReactNode }) {
  if (lead == null && subtitle == null) return null
  return (
    <div className="flex items-center gap-2 min-w-0 short:hidden">
      {lead}
      {subtitle != null && <p className="text-xs text-fg-placeholder truncate min-w-0">{subtitle}</p>}
    </div>
  )
}
